import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import NotificationsInbox from '../components/notifications/NotificationsInbox';
import DevTierSwitcher from '../components/dev/DevTierSwitcher';
import Portrait from '../components/brand/Portrait';
import WorkspaceShell, { isBleedPath } from './WorkspaceShell';
import { OwnedCreatorProvider, OwnedCreatorNotice } from '../contexts/OwnedCreatorContext';
import { PitchWorkspaceProvider } from '../contexts/PitchWorkspaceContext';
import { PadDraftsProvider } from '../components/bearri/roomTypes';
import { useSubscription } from '../contexts/SubscriptionContext';
import { HeaderPlanTag, PastDueBanner } from './HeaderPlanTag';

/**
 * The owner's portrait on the board: the same photo print (or initials on
 * manila) every page uses, and the one way to your profile and settings.
 * It used to hide Clerk's account menu under the print, so a click opened a
 * Clerk popover that only linked back to our own Profile page — one step too
 * many. Now the print IS the link; signing out lives on the profile page.
 */
export function HeaderPortrait() {
  const { user } = useUser();
  const { pathname } = useLocation();
  const name = user?.fullName || user?.username || user?.primaryEmailAddress?.emailAddress || 'You';
  return (
    <Link to="/app/profile" className="br-workspace-avatar" aria-label="Your profile and settings"
      aria-current={pathname === '/app/profile' ? 'page' : undefined}>
      <Portrait name={name} src={user?.hasImage ? user.imageUrl : undefined} size="xs" />
    </Link>
  );
}

export default function MainLayout({children}: {children:React.ReactNode}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { userId } = useAuth();
  // The room (Bearri) runs the full width with no footer, and shows the demo line / channel slip itself:
  // above the room it would push the sticky drawer's foot below the window.
  const bleed = isBleedPath(pathname);
  const { error: planError, loading: planLoading, refreshSubscription } = useSubscription();
  useEffect(() => {
    const onKey = (event:KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        navigate('/app/discover?focus=search');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  // Unsent words (a pitch in progress, each Bearri pad) live under the account key: a sign-out or an account
  // switch remounts them empty, and neither is ever written to browser storage.
  return (
    <OwnedCreatorProvider key={userId || 'signed-out'}>
    <PitchWorkspaceProvider>
    <PadDraftsProvider>
    <WorkspaceShell bleed={bleed} account={<><HeaderPlanTag /><NotificationsInbox /><HeaderPortrait /></>}>
      {!bleed && <OwnedCreatorNotice />}
      {planError ? <div role="alert" className="br-note br-note--amber br-note--straight">{planError} <button disabled={planLoading} onClick={refreshSubscription} className="br-inkline">{planLoading ? 'Checking…' : 'Retry'}</button></div>
        : <PastDueBanner />}
      {children}
      <DevTierSwitcher />
    </WorkspaceShell>
    </PadDraftsProvider>
    </PitchWorkspaceProvider>
    </OwnedCreatorProvider>
  );
}
